import React, { useState, useEffect, useCallback } from "react";
import { Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import HealthCard from "../HealthCard/HealthCard.js";
import DetailedViewHealthCard from "../DetailedViewHealthCard/DetailedViewHealthCard.js";
import arrowdown from "../../../assets/images/arrow-down.svg";
import healthy from "../../../assets/images/healthy.svg";
import critical from "../../../assets/images/critical.svg";
import {
  fetchHealthData,
  fetchDetailedViewData,
  clearDetailedViewAc,
} from "../../../Actions/HealthStatusAction.js";
import "../HealthStatus.scss";
import { subscribeMQTT } from "../../../Actions/mqttActions.js";
import AccessibleTooltip from "../../Tooltip/Tooltip.js";

import {
  getHeadingText,
  getScreenReaderText,
  parseDeviceHealth,
  computeHealthFromData,
  handleCameraKey,
  formatDetailedViewData,
  mapDetailViewToText,
} from "../../../utils/healthStatusUtils.js";

const HealthStatus = () => {
  const [additonalText, setAdditonalText] = useState("");
  const devicesShowArr = [
    "java",
    "mqtt",
    "pc",
    "switch",
    "jace",
    "strobes & speakers",
    "camera",
    "push button",
    "panel",
    "dmp",
    "switch",
    "peplink"
  ]
  const [detailview, setDetailview] = useState(0);
  const [deviceHealth, setDeviceHealth] = useState({});
  const [focusforCardId, setFocusforCardId] = useState("");
  const [screenReaderText, setScreenReaderText] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [dmpData, setDmpData] = useState();
  const [eccData, setECCData] = useState();
  const [pepLinkData, setPepLinkData] = useState();

  const dispatch = useDispatch();
  const { loading, data, detailedViewData, cameraData } = useSelector(
    (state) => state.HealthStatusReducer
  );
  const mqtt = useSelector((state) => state.mqtt);
  const messages = mqtt.messages;

  const unTouchedCards = ["switch"];
  const headingText = getHeadingText(detailview);

  useEffect(() => {
    setScreenReaderText(getScreenReaderText(detailview, headingText));
  }, [detailview, headingText]);

  const clearDetailedView = () => {
    dispatch(clearDetailedViewAc());
    setDetailview(0);
    setAdditonalText("");
    setTimeout(() => {
      document.getElementById(focusforCardId)?.focus();
      setFocusforCardId("");
    }, 1000);
  };
  // ---------------- Update DMP/ECC Data -----------------
  useEffect(() => {
    if (!detailedViewData) return;
    if (String(additonalText).toLowerCase() === "dmp") {
      setDmpData(detailedViewData);
    } else if (String(additonalText).toLowerCase() === "strobes & speakers") {
      setECCData(detailedViewData);
    } else if(String(additonalText).toLowerCase() === "peplink") {
      setPepLinkData(detailedViewData);
    }
  }, [additonalText, detailedViewData]);
  
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        if (tooltipOpen) {
          setTooltipOpen(false);
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    if (detailview !== 0) {
      window.addEventListener("keydown", onKeyDown);
    }
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [detailview, tooltipOpen]);

  const deviceHealthHandler = useCallback((topic, message) => {
    const parsedMessage =
      typeof message === "string" ? JSON.parse(message) : message;
    const healthData = parseDeviceHealth(parsedMessage);
    setDeviceHealth((prev) => ({ ...prev, ...healthData }));
  }, []);

  useEffect(() => {
    const buildingId = parseInt(new URL(window.location.href).pathname.split('/').pop())
    const path = `backend/ldcm/building/device/${buildingId}/#`;
    dispatch(subscribeMQTT(path, deviceHealthHandler));
  }, [deviceHealthHandler, dispatch]);

  useEffect(() => {
    if (messages.length > 0 && mqtt.connected) {
      messages.forEach((message) => {
        if (message.topic.includes("backend/ldcm/building/device")) {
          const parsedMessage =
            typeof message.message === "string"
              ? JSON.parse(message.message)
              : message.message;
          const healthData = {};
          Object.entries(parsedMessage).forEach(([key, value]) => {
            if (value) {
              const deviceKey = handleCameraKey(key);
              if (key.toLowerCase().includes("camera")) return;
              if (key.toLowerCase().includes("dmp")) {
                  const updatedDevices = dmpData?.map((device) =>
                    device.name === value.name
                      ? { ...device, health: value.health ? "true" : "false" }
                      : device
                  );
                  healthData["DMP"] = updatedDevices?.every(
                    (d) => d.health === "true"
                  )
                    ? "healthy"
                    : "critical";
                  setDmpData(updatedDevices);
                  return;
              } if (key.toLowerCase().includes("ecc")) {
                const updatedDevices = eccData?.map((device) =>
                  device.name === value.name
                    ? { ...device, health: value.health ? "true" : "false" }
                    : device
                );
                healthData["Strobes & Speakers"] = updatedDevices?.every(
                  (d) => d.health === "true"
                )
                  ? "healthy"
                  : "critical";
                setECCData(updatedDevices);
                return;
              } if(key.toLowerCase().includes("peplink")) {
                const updatedDevices = pepLinkData?.map((device) =>
                  device.name === value.name
                    ? { ...device, health: value.health ? "true" : "false" }
                    : device
                );
                healthData["PepLink"] = updatedDevices?.every(
                  (d) => d.health === "true"
                )
                  ? "healthy"
                  : "critical";
                setPepLinkData(updatedDevices);
                return;
              } if (key.toLowerCase() === "ups") {
                healthData["UPS"] = value["operating_mode"].includes("mains")
                  ? "healthy"
                  : "critical";
                if (additonalText === "UPS") {
                  dispatch({
                    type: "FETCH_DETAILED_VIEW_SUCCESS",
                    payload: formatDetailedViewData("ups", value),
                  });
                }
              } if (key.includes("pushbutton")) {

                // 1. Find all pushbutton devices from main API data
                const pushButtonsFromAPI =
                  data?.pushbuttons || data?.PushButtons || data?.pushbutton || [];

                // 2. Create a fresh updated list using API list
                const updatedList = Object.values(JSON.parse(pushButtonsFromAPI.data)).map((item) =>
                  item.name === value.name
                    ? { ...item, health: value.health, topic: value.topic }
                    : item
                );

                // 3. If user is inside pushbuttons detailed view, update the redux data
                if (additonalText === "pushbuttons") {
                  dispatch({
                    type: "FETCH_DETAILED_VIEW_SUCCESS",
                    payload: updatedList,
                  });
                }

                // 4. Compute aggregated health ALWAYS
                const allHealthy = updatedList.every((d) => d.health === true);

                healthData["Push Button"] = allHealthy ? "healthy" : "critical";

                return;
              } if (additonalText.toLowerCase() === deviceKey.toLowerCase()) {
                const formatted = formatDetailedViewData(additonalText, value);
                if (Object.keys(formatted).length > 0) {
                  dispatch({
                    type: "FETCH_DETAILED_VIEW_SUCCESS",
                    payload: formatted,
                  });
                }
              }
              healthData[
                key.toLowerCase() === "panel" ? "Panel":
                deviceKey.toLowerCase() === "ecc"
                  ? "Strobes & Speakers"
                  : deviceKey
              ] = value.health ? "healthy" : "critical";
            }
          });
          setDeviceHealth((prev) => ({ ...prev, ...healthData }));
        }
      });
    }
  }, [messages, additonalText, dispatch, mqtt.connected]);

  useEffect(() => {
    const mappedText = mapDetailViewToText(detailview);
    if (mappedText) setAdditonalText(mappedText);
    if (detailview && detailview !== 0 && typeof detailview === "string") {
      setFocusforCardId(detailview.replace(/ /g, "_").toLowerCase());
      dispatch(fetchDetailedViewData(detailview));
    }
  }, [detailview, dispatch]);

  useEffect(() => {
    if (detailview === 0) dispatch(fetchHealthData());
  }, [dispatch, detailview]);

  useEffect(() => {
    if (!loading && data) {
      const healthData = computeHealthFromData(
        data,
        cameraData,
        additonalText,
        setDmpData,
        setECCData,
        setPepLinkData
      );
      setDeviceHealth((prev) => ({ ...prev, ...healthData }));
      setTimeout(() => {
        document?.getElementById("arrowHandlerButton")?.focus();
      }, 200);
    }
  }, [loading, data, cameraData]);
  return (
    <div className="healthStatusWrapper main_ele_wrapper" role="main">
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {screenReaderText}
      </div>

      <div className="main_heading">
        {detailview !== 0 && (
          <AccessibleTooltip
            title="Back"
            open={tooltipOpen}
            customclass="backtooltip"
          >
            <button
              className="arrowHandlerBtn"
              id="arrowHandlerButton"
              onClick={clearDetailedView}
              onFocus={() => setTooltipOpen(true)}
              onBlur={() => setTooltipOpen(false)}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
              data-testid="back-button"
            >
              <img src={arrowdown} alt="" aria-hidden="true" />
              <span className="sr-only">Back to System Health Status</span>
            </button>
          </AccessibleTooltip>
        )}
        <h1 data-testid="health-status-heading">{headingText}</h1>
      </div>

      {detailview === 0 ? (
        <div
          className="inner_Wrapper healthStatus_innerwrap"
          data-testid="main-view"
        >
          <div className="statusColorWrapper">
            <ul aria-hidden="true">
              <li aria-hidden="true">
                <img src={healthy} alt="" className="status-icon" />
                Healthy
              </li>
              <li aria-hidden="true">
                <img src={critical} alt="" className="status-icon" />
                Critical
              </li>
            </ul>
          </div>

          <div className="healthCard_wrapper">
            <Row className="card-size" role="list">
              {Object.entries(deviceHealth).map(([device, status]) => devicesShowArr.includes(device.toLowerCase()) && (
                <HealthCard
                  key={device}
                  id={device}
                  status={status}
                  cardHeading={device}
                  setDetailview={setDetailview}
                  disableDevice={unTouchedCards.includes(device.toLowerCase())}
                  data-testid={`health-card-${device}`}
                />
              ))}
            </Row>
          </div>
        </div>
      ) : (
        <div className="inner_Wrapper" data-testid="detailed-view">
          <DetailedViewHealthCard
            detailedViewCards={
                  String(additonalText).toLowerCase() === "peplink"
                    ? pepLinkData
                    : String(additonalText).toLowerCase() === "dmp"
                    ? dmpData
                    : String(additonalText).toLowerCase() === "strobes & speakers"
                    ? eccData
                    // : String(additonalText).toLowerCase() === "camera"
                    // ? cameraData
                    : detailedViewData
                }
            additonalText={additonalText}
          />
        </div>
      )}
    </div>
  );
};

export default HealthStatus;
